import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

const PROBE_TIMEOUT_MS = 8000;

/**
 * Quickbase REST API host. The realm (e.g. `bandg.quickbase.com`) is NOT part
 * of the URL — it travels in the `QB-Realm-Hostname` header. See
 * {@link quickbaseRealmHostname}. Exported so the `@workspace/ai` Quickbase
 * client reuses the same base rather than re-declaring it.
 */
export const QUICKBASE_API_BASE = "https://api.quickbase.com/v1";

/**
 * B&G's Quickbase realm hostname. Hardcoded to a single tenant and supplied as
 * an app/env var from Key Vault (`QUICKBASE_REALM_HOSTNAME`, e.g.
 * `bandg.quickbase.com`) rather than collected per-user — every connected user
 * authenticates against the same realm, only their user token differs.
 */
export function quickbaseRealmHostname(): string | undefined {
  return process.env.QUICKBASE_REALM_HOSTNAME;
}

/**
 * Confirm the pasted user token can actually reach B&G's Quickbase realm.
 * Quickbase has no lightweight "whoami" endpoint, so we hit `POST /users` and
 * treat only 401 as an invalid key. `POST /users` ("Get users") is an
 * account-level admin endpoint: it returns 403 "Insufficient access" for any
 * token whose owner isn't an account admin, even when the token itself is
 * completely valid — Quickbase still authenticates the token (bumping its Last
 * used timestamp) before denying on permissions. So a 403 means the auth
 * headers were accepted and the token is good; only a 401 means the token was
 * rejected. Any other status — including a 400 for an incomplete body — also
 * means the auth headers were accepted. Mirrors the Procore `validateAccess`
 * probe: one cheap request, its own timeout, actionable failure message instead
 * of a silently-broken "active" connector.
 */
async function validateQuickbaseAccess(
  token: string
): Promise<ConnectorAccessProbeResult> {
  const realm = quickbaseRealmHostname();
  if (!realm) {
    return {
      ok: false,
      message:
        "Quickbase is not configured for this environment (missing realm). " +
        "Contact an administrator.",
    };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(`${QUICKBASE_API_BASE}/users`, {
      method: "POST",
      headers: {
        "QB-Realm-Hostname": realm,
        Authorization: `QB-USER-TOKEN ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({}),
      signal: controller.signal,
    });

    if (response.status === 401) {
      return {
        ok: false,
        message:
          "Quickbase rejected this user token. Create a new token in " +
          "Quickbase (My Preferences → Manage user tokens), make sure it is " +
          "assigned to the app, then reconnect.",
      };
    }

    // Any non-401 status means the realm + token were accepted: 200 on success,
    // 400 for the empty probe body, or 403 when a valid non-admin token lacks
    // access to this account-level endpoint (see doc comment above).
    return { ok: true };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        message: `Quickbase did not respond within ${PROBE_TIMEOUT_MS / 1000}s. Try connecting again.`,
      };
    }
    const detail = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Could not reach Quickbase: ${detail}` };
  } finally {
    clearTimeout(timeout);
  }
}

export const quickbaseConnector: ConnectorDefinition = {
  id: "quickbase",
  name: "Quickbase",
  description: "Read and manage Quickbase apps, tables, fields, and records",
  icon: "quickbase",
  authType: "apikey",
  kind: "custom",
  apiKey: {
    // Single field — `bearerField` defaults to the first field, so the user
    // token lands in `connector.accessToken`. The realm is an env-var secret
    // (see `quickbaseRealmHostname`), not a user-supplied field.
    fields: [
      {
        name: "userToken",
        label: "User token",
        type: "password",
        required: true,
        placeholder: "Paste your Quickbase user token",
        helpText:
          "Create one in Quickbase under My Preferences → Manage user tokens, and assign it to the app you want to use.",
      },
    ],
  },
  // The connector only makes sense once the realm has been provisioned from
  // Key Vault. When the env var is absent the connector is filtered out of the
  // enabled set entirely (same pattern as Procore gating on its client id).
  isEnabled: () => Boolean(quickbaseRealmHostname()),
  validateAccess: validateQuickbaseAccess,
};
